import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NbToastrService } from '@nebular/theme';
import { ServerTokenService } from '../../services/server-token.service';
import { CustomAuthServiceService } from '../../auth/custom-auth-service.service';
import { FaxToEmailService } from './fax-to-email.service';
import { FacilityRow, FaxToEmailMapping } from './fax-to-email.models';
import { FacilitySettingsExtended } from '../facilities-settings/facilities-settings.interface';

@Component({
  selector: 'app-fax-to-email',
  templateUrl: './fax-to-email.component.html',
  styleUrls: ['./fax-to-email.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FaxToEmailComponent implements OnInit, OnDestroy {
  rows: FacilityRow[] = [];
  loading = false;
  showExportPrompt = false;

  editingFacilityId: number | null = null;
  editingEmail = '';
  editingSiteName = '';

  private regionId: number;
  private mappings: FaxToEmailMapping[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private faxService: FaxToEmailService,
    private server: ServerTokenService,
    private authService: CustomAuthServiceService,
    private toastr: NbToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.regionId = this.authService.currentUserSubject?.settings?.regionId;
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.server
      .getWithToken<FacilitySettingsExtended[]>('snfs/admin/getFacilitySettings')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (facilities) => {
          this.loadMappings(facilities);
        },
        error: () => {
          this.loading = false;
          this.toastr.danger('Failed to load facilities', 'Error');
          this.cdr.markForCheck();
        },
      });
  }

  private loadMappings(facilities: FacilitySettingsExtended[]): void {
    const obs$ = this.regionId
      ? this.faxService.getByRegion(this.regionId)
      : this.faxService.getAll();

    obs$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (mappings) => {
        this.mappings = mappings;
        this.buildRows(facilities);
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.mappings = [];
        this.buildRows(facilities);
        this.loading = false;
        this.toastr.danger('Failed to load fax-to-email mappings', 'Error');
        this.cdr.markForCheck();
      },
    });
  }

  private buildRows(facilities: FacilitySettingsExtended[]): void {
    const filtered = this.regionId
      ? facilities.filter((f) => f.regionId === this.regionId)
      : facilities;

    const mappingBySiteId = new Map(this.mappings.map((m) => [m.siteId, m]));

    this.rows = filtered.map((f) => ({
      facilityId: f.facilityId,
      name: f.name || f.displayName || `Facility ${f.facilityId}`,
      referralActive: !!f.referralActive,
      mapping: mappingBySiteId.get(f.facilityId) || null,
    }));
  }

  startEdit(row: FacilityRow): void {
    this.editingFacilityId = row.facilityId;
    this.editingEmail = row.mapping ? row.mapping.email : this.suggestEmail(row);
    this.editingSiteName = row.mapping?.siteName || row.name;
    this.cdr.markForCheck();
  }

  private suggestEmail(row: FacilityRow): string {
    const existing = this.rows.find((r) => r.mapping?.email);
    if (!existing) return '';

    const email = existing.mapping.email;
    const atIndex = email.indexOf('@');
    if (atIndex < 0) return '';

    const localPart = email.substring(0, atIndex);
    const domain = email.substring(atIndex + 1);

    const plusIndex = localPart.indexOf('+');
    const prefix = plusIndex >= 0 ? localPart.substring(0, plusIndex) : localPart;

    const slug = row.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    return `${prefix}+${slug}@${domain}`;
  }

  cancelEdit(): void {
    this.editingFacilityId = null;
    this.editingEmail = '';
    this.editingSiteName = '';
    this.cdr.markForCheck();
  }

  saveEdit(row: FacilityRow): void {
    const email = this.editingEmail.trim();
    const siteName = this.editingSiteName.trim();
    if (!email) {
      this.toastr.warning('Email is required', 'Validation');
      return;
    }

    if (row.mapping) {
      this.faxService
        .update(row.mapping._id, { email, siteName })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (updated) => {
            row.mapping = updated;
            this.editingFacilityId = null;
            this.toastr.success('Mapping updated', 'Success');
            this.cdr.markForCheck();
          },
          error: (err) => {
            this.toastr.danger(
              this.extractError(err, 'Failed to update mapping'),
              'Error',
            );
          },
        });
    } else {
      this.faxService
        .create({
          facilityId: row.facilityId,
          regionId: this.regionId,
          email,
          siteName,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (created) => {
            row.mapping = created;
            this.editingFacilityId = null;
            this.toastr.success('Mapping created', 'Success');
            this.cdr.markForCheck();
          },
          error: (err) => {
            this.toastr.danger(
              this.extractError(err, 'Failed to create mapping'),
              'Error',
            );
          },
        });
    }
  }

  deleteMapping(row: FacilityRow): void {
    if (!row.mapping) return;
    this.faxService
      .delete(row.mapping._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          row.mapping = null;
          this.toastr.success('Mapping removed', 'Success');
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.toastr.danger(
            this.extractError(err, 'Failed to delete mapping'),
            'Error',
          );
        },
      });
  }

  exportCsv(onlyWithFax: boolean): void {
    this.showExportPrompt = false;
    const data = onlyWithFax
      ? this.rows.filter((r) => !!r.mapping)
      : this.rows;

    if (data.length === 0) {
      this.toastr.warning('No data to export', 'Export');
      return;
    }

    const header = 'Facility ID,Facility Name,Referral Active,Site Name,Fax Email';
    const csvRows = data.map((r) => {
      const name = `"${(r.name || '').replace(/"/g, '""')}"`;
      const siteName = `"${(r.mapping?.siteName || '').replace(/"/g, '""')}"`;
      const email = r.mapping?.email || '';
      return `${r.facilityId},${name},${r.referralActive ? 'Yes' : 'No'},${siteName},${email}`;
    });

    const csv = [header, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fax-to-email-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private extractError(err: any, fallback: string): string {
    const msg = err?.error?.error;
    if (typeof msg === 'string' && msg.length > 0 && msg.length < 200) {
      return msg;
    }
    return fallback;
  }

  trackByFacilityId(_i: number, row: FacilityRow): number {
    return row.facilityId;
  }
}
